import { useMemo, useEffect } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { ErrorComponent } from '~/routes/__root';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the AI recommendations page
const ALLOWED_AI_RECOMMENDATIONS_WIDGETS = [
  'ai-recommendations',
  'recommendation-report',
  'allocation',
] as const;

/**
 * Default layout positions for AI recommendations dashboard
 */
const AI_RECOMMENDATIONS_LAYOUT = [
  { h: 16, w: 4, x: 8, y: 0 },
  { h: 16, w: 5, x: 3, y: 0 },
  { h: 16, w: 3, x: 0, y: 0 },
];

const AI_RECOMMENDATIONS_WIDGET_IDS = [
  'ai-recommendations',
  'recommendation-report',
  'allocation',
];

/**
 * Creates widget configs for AI recommendations dashboard with default layout
 * @param withUUID - If true, generates unique IDs with UUIDs; if false, uses simple widget IDs for reset matching
 */
const createAIRecommendationsDashboardConfig = (withUUID = true): Config[] => {
  return AI_RECOMMENDATIONS_LAYOUT.map((position, index) => ({
    ...position,
    id: withUUID
      ? `${AI_RECOMMENDATIONS_WIDGET_IDS[index]}-widget-${crypto.randomUUID()}`
      : AI_RECOMMENDATIONS_WIDGET_IDS[index],
  }));
};

function AIRecommendationsComponent() {
  const { portfolioId } = Route.useParams();
  const setPortfolioId = useSetAtom(portfolioIdAtom);

  // Update portfolioId atom when route changes
  useEffect(() => {
    if (portfolioId) {
      setPortfolioId(portfolioId);
    }
  }, [portfolioId, setPortfolioId]);

  const initialAIRecommendationsConfig = useMemo(
    () => createAIRecommendationsDashboardConfig(true),
    [],
  );
  const aiRecommendationsDefaultLayout = useMemo(
    () => createAIRecommendationsDashboardConfig(false),
    [],
  );

  const { dashboardId, error } = useEnsureDashboard({
    type: 'ai-recommendation',
    name: 'AI Recommendations Dashboard',
    initialConfig: initialAIRecommendationsConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <DashboardPage
      dashboardId={dashboardId || ''}
      breadcrumbLabel="AI Recommendations"
      breadcrumbUrl={`/portfolio/${portfolioId}/ai-recommendations`}
      pageTaglinePrefix="AI Recommendations"
      allowedWidgets={ALLOWED_AI_RECOMMENDATIONS_WIDGETS}
      initialLayout={aiRecommendationsDefaultLayout}
      emptyStateTitle="No AI Recommendation Widgets Found"
      emptyStateMessage="This dashboard doesn't have any AI recommendation widgets yet. Add some AI recommendation widgets to get started."
      emptyStateButtonLabel="Add AI Recommendation Widget"
    />
  );
}

const AIRecommendationsRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <AIRecommendationsComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio/$portfolioId/ai-recommendations',
)({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: AIRecommendationsRoute,
});
